import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!orderId) {
      setStatus('error');
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts >= 8) {
        setStatus('timeout');
        return;
      }
      attempts += 1;
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/payments/cashfree/status/${encodeURIComponent(orderId)}`
        );
        if (!res.ok) throw new Error('status fetch failed');
        const data = await res.json();
        if (cancelled) return;
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
        } else if (data.payment_status === 'failed') {
          setStatus('failed');
        } else {
          setTimeout(poll, 2500);
        }
      } catch (err) {
        if (!cancelled) setStatus('error');
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center relative">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-950/90 via-indigo-950/80 to-black/90" />

      <div className="relative z-10 text-center max-w-md mx-auto px-6">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-purple-400 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-3">Verifying Payment…</h1>
            <p className="text-purple-300">Please wait while we confirm your payment</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div
              className="bg-green-500/20 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-green-400/50"
              style={{ boxShadow: '0 0 40px rgba(34,197,94,0.3)' }}
            >
              <Check className="w-12 h-12 text-green-400" />
            </div>
            <h1
              className="text-3xl font-bold text-white mb-3"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Payment Successful!
            </h1>
            <p className="text-green-300 text-lg mb-2">Thank you for your payment</p>
            {paymentData && paymentData.amount && (
              <p className="text-purple-200 mb-6">
                Amount: {paymentData.currency === 'USD' ? '$' : '₹'}
                {paymentData.amount} {paymentData.currency}
              </p>
            )}
            <p className="text-purple-300 text-sm mb-8">
              You will receive a confirmation on email shortly. Our therapist will reach out to schedule your session.
            </p>
            <Button
              onClick={() => navigate('/home')}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-4 rounded-full"
              data-testid="back-home-btn"
            >
              Back to Home
            </Button>
          </>
        )}

        {(status === 'error' || status === 'failed' || status === 'timeout') && (
          <>
            <div className="bg-red-500/20 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-red-400/50">
              <span className="text-red-400 text-4xl">!</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-3">
              {status === 'timeout'
                ? 'Payment Pending'
                : status === 'failed'
                ? 'Payment Failed'
                : 'Payment Issue'}
            </h1>
            <p className="text-purple-300 mb-6">
              {status === 'timeout'
                ? 'Payment is still processing. Please check your email for confirmation.'
                : status === 'failed'
                ? 'Your payment did not go through. You can try again.'
                : 'Something went wrong while verifying your payment.'}
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => navigate('/home')}
                variant="outline"
                className="border-purple-400/40 text-purple-200 rounded-full"
              >
                Back to Home
              </Button>
              <Button
                onClick={() => navigate('/pay')}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full"
              >
                Try Again
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
